import React from 'react';
import Immutable from 'immutable';
import {
	styled,
	ParentBoundComponent,
	Module,
	DateUtils,
	webapi,
	REPORTED_EXCEPTION,
	Button,
	FormItem,
	TextInput,
	Form,
	FlexBox,
	FlexBoxBlock,
	Icon,
	Cross,
	DateInput
} from 'ps-gui';

import {
	CellMainText,
	CellSubText
} from './portalresultsetmodule';

import {
	getFormItemLabel,
	getPhrase
} from '../data/phrases';
import SubmissionAdminModel, { STATUS_GROUPS, SUBMISSION_ROLES } from './submissionadminmodel';
import { renderDateTimeCell } from './commoncellrenderers';
import {
	Buttons,
	Label,
	ReadOnlyDetail,
	StatusBubble,
	StatusChangeHistory
} from './submissioncomponents';
import {
	downloadFile,
	getGlobalContextChangeChecker,
	stripDia
} from './utils';
import { getLookup } from '../data/lookup';
import {
	Dialog,
	DialogHeader
} from '../components/dialog';
import UploadPane from '../components/uploadpane';
import FileList from '../components/filelist';
import { withGlobalContext } from '../data/globalcontext';
import CheckButton from '../components/checkbutton';

const EMPTY_MAP = Immutable.Map({});

const Wrapper = styled.div`
`;

const DetailContent = styled.div`
	padding: 24px;
`;

const DetailButtonRow = styled.div`
	margin-bottom: 24px;
	.ps-button {
		margin-right: 8px !important;
	}
`;

const QuickFilterHeader = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: ${props => props.theme.disabledTextColor};
	margin-bottom: 8px;
`;

const QuickFilter = styled.div`
	margin-bottom: 24px;
	.checkButton {
		margin-right: 16px;
	}
`;

const EMPTY_LIST = Immutable.List([]);
// test
class SubmissionAdminModule extends ParentBoundComponent {

	constructor() {
		super();
		this.onRowAction = this.onRowAction.bind(this);
		this.downloadFile = this.downloadFile.bind(this);
		this.closeDetail = this.closeDetail.bind(this);
		this.hideStatusChangeForm = this.hideStatusChangeForm.bind(this);
		this.onStatusChangeFileUploadRequested = this.onStatusChangeFileUploadRequested.bind(this);
		this.applyStatusChange = this.applyStatusChange.bind(this);
		this.export = this.export.bind(this);
		this.checkGlobalContextChange = getGlobalContextChangeChecker(this);
		this.customResultProps = Object.assign({withRowSelection: true}, customResultProps);
		this.saveDeadline = this.saveDeadline.bind(this);
		this.customResultProps.toolBar =
			<Icon
				name="refresh"
				active
				hint={getPhrase('refresh')}
				onMouseDown={async () => {
					this.setModel(await this.getModel().refreshSearch(true));
				}}
			/>;
	}

	async saveDeadline() {
		this.setModel(await this.getModel().saveDeadline());
	}

	async export() {
		let model = this.getModel();
		const res = await webapi.execute('/api/submission/export', {
			submissionId: model.getDisplayedSubmissionId()
		});
		if (res === REPORTED_EXCEPTION) {
			return;
		}
		downloadFile(res.fileId, stripDia(res.originalFileName));
	}

	async applyStatusChange() {
		this.setModel(await this.getModel().applyStatusChange());
	}

	closeDetail() {
		this.setModel(this.getModel().closeDetail());
	}

	getModel() {
		return new SubmissionAdminModel(this.getData());
	}

	setModel(model) {
		this.setData(model.getData());
	}

	componentDidMount() {
		this.checkGlobalContextChange();
	}

	componentDidUpdate(prevProps) {
		this.checkGlobalContextChange(prevProps);
	}

	async openDetail(id) {
		this.setModel(await this.getModel().openDetail(id));
	}

	async onRowAction(row, rowIndex, options) {
		const id = row.get('id');
		this.openDetail(id);
	}

	async setStatusGroupFilter(key, value) {
		this.setModel(await this.getModel().setStatusGroupFilter(key, value));
	}

	async setRoleFilter(key, value) {
		this.setModel(await this.getModel().setRoleFilter(key, value));
	}

	renderList() {
		const data = this.getData();
		const statusGroupFilter = data.get('statusGroupFilter') || EMPTY_MAP;
		const roleFilter = data.get('roleFilter') || EMPTY_MAP;
		return (
			<div>
				<QuickFilter>
					<QuickFilterHeader>{getPhrase('displayedSubmissions')}</QuickFilterHeader>
					{
						STATUS_GROUPS.map(key => {
							return (
								<CheckButton
									key={key}
									text={getPhrase('submissionStatusGroup.' + key)}
									value={statusGroupFilter.get(key)}
									onChange={value => this.setStatusGroupFilter(key, value)}
								/>
							);
						})
					}
					{
						SUBMISSION_ROLES.map((key, index) => {
							return (
								<CheckButton
									key={key}
									text={getPhrase('submissionRole.' + key)}
									value={roleFilter.get(key)}
									onChange={value => this.setRoleFilter(key, value)}
									marginLeft={index === 0 ? '64px' : null}
								/>
							);
						})
					}
				</QuickFilter>
				<Module
					appState={data.get('search')}
					onDataChange={newData => {
						this.setData(data.setIn(['search', 'data'], newData));
					}}
					customResultProps={this.customResultProps}
					onRowAction={this.onRowAction}
				//leftToolBar={this.renderCandidateListToolbar}
				//onResultIconMouseDown={this.onCandidateIconMouseDown}
				/>
			</div>
		);
	}

	async downloadFile(file) {
		const uf = await webapi.execute('/api/submission/getAttachmentDownloadInfo', {
			submissionId: this.getModel().getDisplayedSubmissionId(),
			seqId: file.get('seqId')
		});
		if (uf === REPORTED_EXCEPTION) {
			return;
		}
		downloadFile(uf.fileId, uf.originalFileName);
	}

	setStatusChangeProperty(name, value) {
		this.setModel(this.getModel().setStatusChangeProperty(name, value));
	}

	showStatusChangeForm(statusId) {
		this.setModel(this.getModel().showStatusChangeForm(statusId));
	}

	hideStatusChangeForm() {
		this.setModel(this.getModel().hideStatusChangeForm());
	}

	async onStatusChangeFileUploadRequested(files) {

		if (!files || files.length === 0) {
			return;
		}

		let newFiles = [];
		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			const uploadedFile = await webapi.upload('/api/file', file);
			if (uploadedFile === REPORTED_EXCEPTION) {
				continue;
			}
			newFiles.push(Object.assign({
				size: file.size,
				fileName: uploadedFile.originalFileName
			}, uploadedFile));
		}
		this.setStatusChangeProperty('files', (this.getData().getIn(['statusChangeForm', 'files']) || EMPTY_LIST).concat(Immutable.fromJS(newFiles)));
	}

	renderStatusChangeForm(scf) {
		if (!scf) {
			return null;
		}
		const statusId = scf.get('statusId');
		return (
			<Dialog onCloseRequested={this.hideStatusChangeForm}>
				<DialogHeader>{getPhrase('submissionStatusChange')}</DialogHeader>
				<Form vertical>
					<FormItem label={getFormItemLabel('comments')} minWidth="640px">
						<TextInput
							multiline
							value={scf.get('comments')}
							onChange={value => {
								this.setStatusChangeProperty('comments', value);
							}}
							minHeight="120px"
							resize="vertical"
							maxLength="1000"
						/>
					</FormItem>
					{
						statusId === 'RETURNED' && (
							<Cross>
								<FormItem label={getFormItemLabel('deadline')} margin="4px 4px 16px 4px" width="200px">
									<DateInput
										value={scf.get('deadline')}
										onChange={value => {
											this.setStatusChangeProperty('deadline', value);
										}}
									/>
								</FormItem>
							</Cross>
						)
					}
					<UploadPane
						onFileUploadRequested={this.onStatusChangeFileUploadRequested}
						buttonText={getPhrase('selectAttachment')}
						dragText={getPhrase('attachANoteOrAFile')}
						marginBottom="24px"
						multiple
					/>
					<FileList
						files={scf.get('files')}
						onDeleteRequested={(file, index) => {
							this.setModel(this.getModel().toggleStatusChangeFileDeleted(index));
						}}
					/>
					<Buttons>
						<Button
							text={getLookup('submissionStatus').getText(statusId)}
							onAction={this.applyStatusChange}
						/>
						<Button
							text={getPhrase('close')}
							onAction={this.hideStatusChangeForm}
							negative
							type="light"
						/>
					</Buttons>
				</Form>
			</Dialog>
		);
	}

	renderDetailButtons(detail) {
		const statusId = detail.get('statusId');
		const withStateTransfers = statusId === 'SUBMITTED' || statusId === 'IN_PROGRESS';
		const lookup = getLookup('submissionStatus');
		return (
			<>
				<DetailButtonRow>
					<Button
						text={getPhrase('export')}
						onAction={this.export}
					/>
				</DetailButtonRow>
				{
					statusId === 'RETURNED' && (
						<DetailButtonRow>
							<FormItem label={getFormItemLabel('deadline')}>
								<DateInput
									value={detail.get('deadline')}
									onChange={value => {
										this.setModel(this.getModel().setDeadline(value));
									}}
								/>
							</FormItem>
							<Button
								text={getPhrase('setDeadline')}
								onAction={this.saveDeadline}
								disabled={DateUtils.compareDates(detail.get('deadline'), detail.get('originalDeadline')) === 0}
							/>
						</DetailButtonRow>
					)
				}
				{
					withStateTransfers &&
					<DetailButtonRow>
						<Label>{getPhrase('setSubmissionStateTo')}:</Label>
						{
							[
								'IN_PROGRESS',
								'RETURNED',
								'COMPLETED',
								'DENIED',
							].map(toStatusId => {
								if (statusId === toStatusId) {
									return null;
								}
								return <Button
									key={toStatusId}
									text={lookup.getText(toStatusId)}
									negative={toStatusId === 'RETURNED' || toStatusId === 'DENIED'}
									onAction={() => {
										this.showStatusChangeForm(toStatusId);
									}}
									type="medium"
									marginRight="8px"
								/>;
							})
						}
					</DetailButtonRow>

				}
				<DetailButtonRow>
					<Button
						text={getPhrase('backToOverview')}
						onAction={this.closeDetail}
					/>
				</DetailButtonRow>
			</>
		);
	}

	renderDetail() {
		const data = this.getData();
		const detail = data.get('detail');
		return (
			<DetailContent>
				<FlexBox>
					<FlexBoxBlock flexGrow="1">
						<ReadOnlyDetail
							detail={detail}
							withUser
							onFileDownloadRequested={this.downloadFile}
							buttons={this.renderDetailButtons(detail)}
						/>
					</FlexBoxBlock>
					<FlexBoxBlock flexGrow="1">
						<StatusChangeHistory
							transitions={detail.get('transitions')}
							withCreatedBy
							withDiffs
						/>
					</FlexBoxBlock>
				</FlexBox>
			</DetailContent>
		);
	}

	render() {
		const data = this.getData();
		return (
			<Wrapper>
				{data.get('detail') ? this.renderDetail() : this.renderList()}
				{
					this.renderStatusChangeForm(data.get('statusChangeForm'))
				}
			</Wrapper>
		);
	}
}

const cellRendererMap = Object.freeze({
	'name': (cell, row, colIndex) => {
		return (
			<CellMainText className={row.get('status_id') === 'CANCELLED' ? 'deleted' : ''}>{cell}</CellMainText>
		);
	},
	'role_id': (cell, row, colIndex) => {
		return (
			<>
				<CellMainText className="secondary">{cell}</CellMainText>
				<CellSubText>{row.get('party_id')} - {row.get('role_type')}</CellSubText>
			</>
		);
	},
	'status': (cell, row) => {
		return (
			<StatusBubble className={row.get('status_id')}>{cell}</StatusBubble>
		);
	},
	submitted_by: (cell, row) => {
		return (
			<>
				<CellMainText className="secondary">{cell}</CellMainText>
				<CellSubText>{row.get('user_name')}</CellSubText>
			</>
		);
	},
	processed_by: (cell, row) => {
		return (
			<>
				<CellMainText className="secondary">{cell}</CellMainText>
				<CellSubText>{row.get('processed_by_user_name')}</CellSubText>
			</>
		);
	},
	last_modified: renderDateTimeCell,
	deadline: renderDateTimeCell,
	'submitted_when': (cell, row, colIndex) => {
		if (cell) {
			return renderDateTimeCell(cell, row, colIndex);
		} else {
			return (
				<i>
					{renderDateTimeCell(DateUtils.formatDate(row.get('created_when')), row, colIndex)}
				</i>
			);
		}
	}
});

const customResultProps = {
	cellRendererMap,
	defaultColumnWidths: {
		'submitted_when': '120px',
		'submitted_by': '180px',
		'processed_by': '180px',
		last_modified: '120px',
		'status': '180px',
		type: '240px',
		submission_id: '140px',
		deadline: '120px'
	},
	columnFilters: {
		'submission_id': {
			type: 'text',
		},
		role_id: {
			type: 'lookup',
			lookup: 'partyIdName'
		},
		'status': {
			type: 'lookup',
			lookup: 'submissionStatus'
		},
		'type': {
			type: 'lookup',
			lookup: 'submissionType'
		},
		'submitted_when': {
			type: 'date'
		},
		last_modified: {
			type: 'date'
		},
		name: {
			type: 'text'
		},
		submitted_by: {
			type: 'lookup',
			lookup: 'user'
		},
		processed_by: {
			type: 'lookup',
			lookup: 'submissionProcessedBy'
		},
		deadline: {
			type: 'date'
		}
	}
};

export default withGlobalContext(SubmissionAdminModule);
